import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Prompt } from 'react-router-dom';

import type { DxfSceneState } from '@frontend/api';

import FetchingInfoScreen from './FetchingInfoScreen';
import ImgViewer from './ImgViewer';
import MalwareDetectedScreen from './MalwareDetectedScreen';
import { NoFileToDisplayScreen } from './NoFileToDisplayScreen';
import { ProcessingFailedScreen } from './ProcessingFailedScreen';
import UnsupportedFileScreen from './UnsupportedFileScreen';
import VideoViewer from './VideoViewer';
import CADViewer from './cadViewer/CADViewer';
import {
  getAreCaliperToolSelectionsPresent,
  getAreSelectorToolSelectionsPresent,
} from './cadViewer/data/reducer';
import {
  getActiveFile,
  getActivePartPmiUrl,
  getIsFetchingViewerModel,
} from './data/reducer';
import { DXFViewer } from './dxfViewer/DXFViewer';
import { PDFViewer } from './pdfViewer/PDFViewer';
import { getAreUnsavedPdfAnnotationsPresent } from './pdfViewer/data/reducer';
import type { ViewerConfig } from './pdfViewer/viewerConfig';
import {
  VIEWER_KINDS,
  type ViewerKindName,
  type ViewerKind,
} from './resolveViewerKind';

type ViewerProps = {
  config: ViewerConfig;
  leftOffset?: string;
  viewerKind: ViewerKind;
};

/**
 * Latches `true` the first time `isActive` is true and never goes back.
 */
const useMountLatch = (isActive: boolean) => {
  const [hasMounted, setHasMounted] = useState(isActive);
  useEffect(() => {
    if (!hasMounted && isActive) {
      setHasMounted(true);
    }
  }, [hasMounted, isActive]);
  return hasMounted;
};

/**
 * The viewers that aren't backed by a latched engine — cheap to mount and unmount
 * freely. Engine-backed kinds (MODEL, PDF, DXF_SCENE) and MALWARE render
 * elsewhere, so they resolve to nothing here.
 */
const renderNonEngineViewer = (kind: ViewerKindName) => {
  switch (kind) {
    case VIEWER_KINDS.IMAGE:
      return <ImgViewer />;
    case VIEWER_KINDS.VIDEO:
      return <VideoViewer />;
    case VIEWER_KINDS.NO_FILE:
      return <NoFileToDisplayScreen key="noFileToDisplay" />;
    case VIEWER_KINDS.UNSUPPORTED:
      return <UnsupportedFileScreen key="UnsupportedFile" />;
    default:
      return null;
  }
};

const Viewer = ({ leftOffset, config, viewerKind }: ViewerProps) => {
  const activeFile = useSelector(getActiveFile);
  const activePartPmiUrl = useSelector(getActivePartPmiUrl);
  const areCaliperToolSelectionsPresent = useSelector(
    getAreCaliperToolSelectionsPresent,
  );
  const areSelectorToolSelectionsPresent = useSelector(
    getAreSelectorToolSelectionsPresent,
  );
  const areUnsavedPdfAnnotationsPresent = useSelector(
    getAreUnsavedPdfAnnotationsPresent,
  );
  const isFetchingViewerModel = useSelector(getIsFetchingViewerModel);

  const { filename, processingFailed, uuid } = activeFile ?? {};

  const [hasShownFetching, setHasShownFetching] = useState(false);
  const [pmiUrlByPartFileUuid, setPmiUrlByPartFileUuid] = useState({});

  /**
   * The CAD, PDF, and DXF engines are expensive to instantiate and — in PDFTron's case —
   * unmounting the WebViewer tears down the page, so both are mounted at most once
   * per viewer session and hidden via `isPaused` when another viewer takes over.
   */
  const shouldLoadCAD = useMountLatch(viewerKind.kind === VIEWER_KINDS.MODEL);
  const shouldRenderPDFViewer = useMountLatch(
    viewerKind.kind === VIEWER_KINDS.PDF,
  );
  const isDxfScene = viewerKind.kind === VIEWER_KINDS.DXF_SCENE;
  const shouldRenderDxfScene = useMountLatch(isDxfScene);
  // Keep the scene while another viewer is active so pausing does not unload the
  // drawing and wipe mapping state the session is meant to retain.
  const [latchedDxfScene, setLatchedDxfScene] = useState<{
    partFileUuid: string;
    scene: DxfSceneState;
  } | null>(null);

  useEffect(() => {
    if (viewerKind.kind !== VIEWER_KINDS.DXF_SCENE || !uuid) {
      return;
    }
    setLatchedDxfScene({ partFileUuid: uuid, scene: viewerKind.scene });
  }, [viewerKind, uuid]);

  const dxfScene = isDxfScene
    ? viewerKind.scene
    : latchedDxfScene?.partFileUuid === uuid
    ? latchedDxfScene?.scene ?? null
    : null;

  useEffect(() => {
    if (!uuid) {
      return;
    }
    const activePartFileUuid = uuid;
    if (!(activePartFileUuid in pmiUrlByPartFileUuid)) {
      setPmiUrlByPartFileUuid({
        ...pmiUrlByPartFileUuid,
        [activePartFileUuid]: activePartPmiUrl,
      });
    }
  }, [pmiUrlByPartFileUuid, activePartPmiUrl, uuid]);

  useEffect(() => {
    setHasShownFetching(
      (_hasShownFetching) => !isFetchingViewerModel && !_hasShownFetching,
    );
  }, [isFetchingViewerModel, setHasShownFetching]);

  return (
    <>
      <Prompt
        when={
          !!(
            areUnsavedPdfAnnotationsPresent ||
            areCaliperToolSelectionsPresent ||
            areSelectorToolSelectionsPresent
          )
        }
        message={
          'You have unsaved annotations. Are you sure you want to continue?'
        }
      />
      {viewerKind.kind === VIEWER_KINDS.MALWARE ? (
        <MalwareDetectedScreen />
      ) : (
        <>
          {isFetchingViewerModel && !hasShownFetching && <FetchingInfoScreen />}
          {/* Latched engines: mounted once, paused rather than unmounted.
              PDF/DXF pause with visibility:hidden (still in flow), so they
              overlay; CAD uses display:none and is first in the DOM. */}
          {shouldLoadCAD && (
            <CADViewer
              leftOffset={leftOffset}
              isPaused={viewerKind.kind !== VIEWER_KINDS.MODEL}
              pmiUrlByPartFileUuid={pmiUrlByPartFileUuid}
            />
          )}
          {shouldRenderPDFViewer && (
            <PDFViewer
              activeFileUuid={uuid ?? null}
              filename={filename ?? null}
              url={viewerKind.kind === VIEWER_KINDS.PDF ? viewerKind.url : null}
              isPaused={viewerKind.kind !== VIEWER_KINDS.PDF}
              config={config}
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              visibility={
                viewerKind.kind !== VIEWER_KINDS.PDF ? 'hidden' : 'unset'
              }
              sx={{
                // WebViewer's shadow DOM sets `visibility: visible` on its own
                // nodes, overriding the inherited `hidden` above, so the paused
                // PDF kept painting over the image/video viewer underneath.
                // `content-visibility` skips painting the subtree regardless.
                contentVisibility:
                  viewerKind.kind !== VIEWER_KINDS.PDF ? 'hidden' : 'visible',
                '@container viewer-content (max-width: 991px)': {
                  top: '50px',
                },
              }}
            />
          )}
          {shouldRenderDxfScene && (
            <DXFViewer
              partFileUuid={uuid ?? null}
              scene={dxfScene}
              isPaused={!isDxfScene}
              /* Nothing on the drawing surface is selectable text, so a press here should
         never anchor a selection to drag out of. Permanent rather than applied
         during a gesture: by the time a gesture is recognised, the anchor is
         already placed. */
              userSelect="none"
              position="absolute"
              inset={0}
              visibility={!isDxfScene ? 'hidden' : 'unset'}
            />
          )}
          {processingFailed ? (
            <ProcessingFailedScreen key="ProcessingFailed" />
          ) : (
            renderNonEngineViewer(viewerKind.kind)
          )}
        </>
      )}
    </>
  );
};

export default Viewer;
